import { useCallback, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { errorToast } from "@/v2-ui/toast"
import cn from "@/v2-ui/utils/utils.cn"
import {
  clearDriftFixParams,
  readDriftFixIntent,
} from "@/v2-backoffice-client/layer/drift/resolve/layer.drift.resolve"
import FeatureHotfixNotice from "@/v2-backoffice-client/feature/FeatureHotfixNotice"
import LayerWorkbench from "@/v2-backoffice-client/layer/update/LayerWorkbench"
import {
  getConfigurationServiceUrl,
  inferConfigurationLayerType,
} from "@/v2-backoffice-client/layer/service/layer.service.metadata"
import type { LayerFragment } from "@/v2-backoffice-client/layer/__types__/layer.fragment"
import { useLayerUpdateMutation } from "@/v2-backoffice-client/layer/update/layer.update.mutation"
import { LAYER_DRIFT_QUERY_NAME } from "@/v2-backoffice-client/layer/layer.constants"
import {
  getLayerEditorModelPath,
  type LayerEditorField,
} from "@/v2-backoffice-client/layer/update/schema/layer.update.schema"

export type LayerUpdateEditorProps = {
  className?: string
  field: LayerEditorField
  layer: LayerFragment | null | undefined
}

/**
 * The runtime-catalog binding of the layer workbench: saves against the
 * userland gis.Layers row; the normalized cache updates every tab from the
 * mutation response.
 */
function LayerUpdateEditor(props: LayerUpdateEditorProps) {
  const { layer, field, className } = props
  const [error, setError] = useState<Error | undefined>()

  const [updateLayer, { loading }] = useLayerUpdateMutation({
    onError: setError,
    // Drift severity and remediation are derived from the layer's saved
    // documents, so a save is what resolves them. Without this the Drift
    // tab keeps offering a fix the operator has just made.
    refetchQueries: [LAYER_DRIFT_QUERY_NAME],
  })

  errorToast(error, {
    title: "Failed to update layer",
  })

  // The Drift tab hands a fix over through the URL, so only the document
  // the link addressed picks one up.
  const [searchParams, setSearchParams] = useSearchParams()
  const driftFix = readDriftFixIntent(searchParams, field)

  // Dropped once staged: the edit now lives in the editor, and a reload
  // that re-applied it would silently undo an operator's undo.
  const onDriftFixApplied = useCallback(() => {
    setSearchParams((previous) => clearDriftFixParams(previous), {
      replace: true,
    })
  }, [setSearchParams])

  if (!layer) return null

  function saveField(fieldName: string, value: unknown) {
    // The configuration's shape wins over the stored type: saving a config
    // that changed layer class (group -> feature, ...) carries the new
    // LayerTypeCode along, or the console would build the wrong layer
    // class from the stale code and render nothing.
    const inferred =
      fieldName === "Configuration" ? inferConfigurationLayerType(value) : null
    const typeSync =
      inferred && inferred !== layer!.LayerTypeCode
        ? { LayerTypeCode: inferred }
        : undefined

    return updateLayer({
      variables: {
        input: {
          FeatureCode: layer!.FeatureCode,
          [fieldName]: value,
          ...typeSync,
        },
      },
    })
  }

  const isConfiguration = field === "Configuration"

  return (
    <div
      className={cn(
        "layer-update-editor flex min-h-0 flex-1 flex-col",
        className,
      )}
    >
      <FeatureHotfixNotice className="mt-2" />
      <LayerWorkbench
        field={field}
        layerTypeCode={layer.LayerTypeCode}
        modelPath={getLayerEditorModelPath(field, layer.LayerTypeCode)}
        value={JSON.stringify(layer[field], null, 2)}
        isSaving={loading}
        onSave={(value) => saveField(field, value)}
        drift={{
          featureCode: layer.FeatureCode,
          fix: driftFix,
          onFixApplied: onDriftFixApplied,
        }}
        // From the SAVED configuration: guided widgets on the Filters tab
        // need the service even though that tab never edits the url.
        serviceUrl={getConfigurationServiceUrl(layer.Configuration)}
        preview={
          isConfiguration
            ? {
                FeatureCode: layer.FeatureCode,
                Name: layer.Name,
                LayerTypeCode: layer.LayerTypeCode,
                CreatedAt: layer.CreatedAt,
                Filters: layer.Filters,
                Interactions: layer.Interactions,
                Export: layer.Export,
                Table: layer.Table,
              }
            : undefined
        }
        assist={
          isConfiguration
            ? {
                featureCode: layer.FeatureCode,
                name: layer.Name,
                blobs: {
                  Configuration: layer.Configuration,
                  Filters: layer.Filters,
                  Interactions: layer.Interactions,
                  Export: layer.Export,
                  Table: layer.Table,
                },
                isSavingField: loading,
                onSaveField: saveField,
              }
            : undefined
        }
      />
    </div>
  )
}

export default LayerUpdateEditor
